import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { clearAuth, fetchWithAuth } from "../../auth/auth";
import { getApiUrl } from "../../utils/getApiUrl";
import { BaseModal } from "../../components/modalCommon/modalBase";
import { ModalHeader } from "../../components/modalCommon/modalHeader";
import { ModalBody } from "../../components/modalCommon/modalBody";
import { ModalFooter } from "../../components/modalCommon/modalFooter";
import { useI18n } from "../../utils/translate/i18n/useI18n";
import "./deleteAccountModal.scss";
import type { DeleteAccountModalModel } from "../../type/user";


const CONFIRM_PHRASE = "DELETE";

const DeleteAccountModal: React.FC<DeleteAccountModalModel> = ({
  isOpen,
  onClose,
  userId,
  username,
}) => {
  const { t } = useI18n();
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleClose = () => {
    setConfirmText("");
    setError(null);
    onClose();
  };

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    if (confirmText.trim().toUpperCase() !== CONFIRM_PHRASE) {
      setError(`${t("Aby potwierdzić, wpisz")} "${CONFIRM_PHRASE}".`);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithAuth(
        `${getApiUrl("user/delete")}/${userId}?permRemoval=true`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          (data as { error?: string }).error ?? `HTTP ${res.status}`
        );
      }
      clearAuth();
      navigate("/login");
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : t("Usunięcie konta nie powiodło się.")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal open={isOpen} onClose={handleClose} size="sm">
      <ModalHeader title={t("Usuń konto")} onClose={handleClose} />

      <form onSubmit={handleDelete}>
        <ModalBody>
          <div className="delete-account-modal__warning">
            <i className="bi bi-exclamation-triangle-fill delete-account-modal__warning-icon"></i>
            <p className="delete-account-modal__warning-title">
              {t("Tej operacji nie można cofnąć")}
            </p>
            <p className="delete-account-modal__warning-desc">
              {`${t("Usuń konto")} ${username}. ${t("Trwałe usunięcie konta spowoduje utratę wszystkich danych.")}`}
            </p>
          </div>

          <div className="delete-account-modal__confirm-field">
            <label htmlFor="delete-confirm-input">
              {t("Aby potwierdzić, wpisz")}{" "}
              <strong className="delete-account-modal__phrase">
                {CONFIRM_PHRASE}
              </strong>
            </label>
            <input
              id="delete-confirm-input"
              type="text"
              className="form-control delete-account-modal__input"
              value={confirmText}
              onChange={(e) => {
                setConfirmText(e.target.value);
                setError(null);
              }}
              autoComplete="off"
              autoFocus
            />
          </div>

          {error && (
            <div className="delete-account-modal__error">
              <i className="bi bi-exclamation-circle-fill me-1"></i>
              {error}
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {t("Anuluj")}
          </button>
          <button
            type="submit"
            className="btn btn-danger delete-account-modal__submit-btn"
            disabled={
              loading ||
              confirmText.trim().toUpperCase() !== CONFIRM_PHRASE
            }
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {t("Usuwanie…")}
              </>
            ) : (
              <>
                <i className="bi bi-trash3-fill me-2"></i>
                {t("Usuń konto")}
              </>
            )}
          </button>
        </ModalFooter>
      </form>
    </BaseModal>
  );
};

export default DeleteAccountModal;
